import React, { useState } from "react";
import { Button } from "../../../components/ui";
import ComingSoonPitch, {
  Benefit,
} from "../../shared/components/ComingSoonPitch";
import EarlyAccessModal from "../components/EarlyAccessModal";
import useEarlyAccess from "../hooks/useEarlyAccess";
// The browser mock is the design team's own artwork, taken in whole rather than
// rebuilt out of markup: it is a picture of the portfolio page, not a working
// preview of one, and nothing on this screen reads or changes a pixel of it.
import portfolioMock from "../../../assets/svg/create page/preview-column.svg";
import professionalIcon from "../../../assets/svg/create page/create-1.svg";
import expertiseIcon from "../../../assets/svg/create page/create-2.svg";
import enquiryFormIcon from "../../../assets/svg/create page/create-3.svg";
import crownIcon from "../../../assets/svg/create page/crown icon.svg";

/**
 * The three the design leads with, in the order a visitor to the page would
 * meet them: the page looks right, it says what you do, and it turns whoever
 * read that into a consultation.
 */
const BENEFITS: Benefit[] = [
  {
    icon: professionalIcon,
    title: "Look professional",
    detail: "A polished page clients trust at first glance",
  },
  {
    icon: expertiseIcon,
    title: "Showcase your expertise",
    detail: "Highlight specializations, services and results",
  },
  {
    icon: enquiryFormIcon,
    title: "In built enquiry form",
    detail: "Turn visitors into booked consultations",
  },
];

/**
 * The public portfolio page, before it exists: what it will be, shown rather
 * than told.
 *
 * Unlike the client app's screen this one ends in a way in — not to a screen
 * that exists, since none does, but to putting their hand up for the one that
 * will. `EarlyAccessModal` says what that gets them before it counts anybody
 * in, and once they have said yes the button is gone for good: the offer has
 * been taken, and a page that keeps asking reads as one that did not hear.
 */
const CreatePagePage: React.FC = () => {
  const { joined, join } = useEarlyAccess();
  const [asking, setAsking] = useState(false);

  return (
    <ComingSoonPitch
      title="My profile page"
      heading="Launch your portfolio page"
      subheading={
        <>
          A personalised portfolio for your practice in{" "}
          {/* The claim the line is making. Emphasised as the design has it —
              fifteen minutes is the part somebody is deciding on. */}
          <strong className="font-semibold text-ink">15 min</strong>
        </>
      }
      benefits={BENEFITS}
      action={
        joined ? (
          // What stands where the button was. A statement rather than anything
          // pressable: there is nothing left to do here until the page exists.
          <p className="flex items-center gap-2.5 text-[15px] 3xl:text-[17px] font-semibold text-ink">
            {/* Empty alt — the crown says the same thing as the words beside
                it, and a screen reader reading both would say it twice. */}
            <img
              src={crownIcon}
              alt=""
              className="h-[30px] w-[30px] flex-shrink-0"
            />
            You&apos;re on priority list!
          </p>
        ) : (
          <>
            <Button
              variant="primary"
              size="md"
              onClick={() => setAsking(true)}
            >
              Get early access
            </Button>

            {/* Portals to the body, so it does not matter that it is declared
                inside the pitch's action slot — see `Modal`. */}
            <EarlyAccessModal
              open={asking}
              onJoin={() => {
                join();
                setAsking(false);
              }}
              onClose={() => setAsking(false)}
            />
          </>
        )
      }
      artwork={
        // The browser window runs off the right edge of the card, as the design
        // has it: a page cropped by the card rather than shrunk to fit inside
        // it — a browser mock small enough to fit whole stops reading as a
        // website and starts reading as an icon.
        //
        // It is set at a width and left at whatever height that gives it, sat
        // against the bottom of the card. The card is cut to suit — see the
        // height on it in `ComingSoonPitch` — so the mock meets the bottom
        // border at its own scale, with only its right-hand side cropped.
        <div className="overflow-hidden px-6 pt-8 sm:px-10 lg:flex lg:h-full lg:items-end lg:px-0">
          {/* `max-w-none` so the image keeps its own width instead of being
              fitted to the half-card it sits in, and `shrink-0` so the width it
              asks for is allowed to overflow that cell — a flex item would
              otherwise be squeezed back to fit and the drawing squashed. */}
          <img
            src={portfolioMock}
            alt="A portfolio page in a browser window, with a branded header and a row of service cards"
            className="h-auto w-full shrink-0 lg:w-[700px] lg:max-w-none 3xl:w-[860px]"
          />
        </div>
      }
    />
  );
};

export default CreatePagePage;
